import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import {
  Plus, 
  Trash2, 
  Edit, 
  ClipboardCheck, 
  ArrowLeft,
  GripVertical,
  AlertTriangle,
  CheckCircle,
  Loader2,
  FileText,
  Settings,
  Eye,
  Copy
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import OperatorEvaluation from '@/components/OperatorEvaluation';

const AdminInspectionTemplates = ({ user }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState({ builtin: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state for creating/editing templates
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'clipboard',
    category: 'custom',
    require_equipment_id: true,
    require_mileage_hours: false,
    require_location: true
  });
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItem, setNewItem] = useState({ category: '', item: '', critical: false });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/inspections/all-templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error(t('adminToasts.FailedToLoadTemplates'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'clipboard',
      category: 'custom',
      require_equipment_id: true,
      require_mileage_hours: false,
      require_location: true
    });
    setChecklistItems([]);
    setNewItem({ category: '', item: '', critical: false });
    setShowCreateDialog(true);
  };

  const openEditDialog = async (template) => {
    try {
      // Fetch full template details
      const res = await axiosInstance.get(`/inspections/custom-templates/${template.id}`);
      const fullTemplate = res.data;
      
      setFormData({
        name: fullTemplate.name,
        description: fullTemplate.description || '',
        icon: fullTemplate.icon || 'clipboard',
        category: fullTemplate.category || 'custom',
        require_equipment_id: fullTemplate.require_equipment_id ?? true,
        require_mileage_hours: fullTemplate.require_mileage_hours ?? false,
        require_location: fullTemplate.require_location ?? true
      });
      setChecklistItems(fullTemplate.checklist || []);
      setSelectedTemplate(fullTemplate);
      setShowEditDialog(true);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error(t('adminToasts.FailedToLoadTemplateDetails'));
    }
  };

  const addChecklistItem = () => {
    if (!newItem.category.trim() || !newItem.item.trim()) {
      toast.error(t('adminToasts.PleaseFillInBothCategoryAndItem'));
      return;
    }
    
    setChecklistItems([...checklistItems, {
      id: `custom_${checklistItems.length + 1}`,
      category: newItem.category.trim(),
      item: newItem.item.trim(),
      critical: newItem.critical
    }]);
    setNewItem({ category: newItem.category, item: '', critical: false });
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const toggleItemCritical = (index) => {
    const updated = [...checklistItems];
    updated[index].critical = !updated[index].critical;
    setChecklistItems(updated);
  };

  const createTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error(t('adminToasts.PleaseEnterATemplateName'));
      return;
    }
    if (checklistItems.length === 0) {
      toast.error(t('adminToasts.PleaseAddAtLeastOneChecklistItem'));
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.post('/inspections/custom-templates', {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        category: formData.category,
        checklist: checklistItems.map(item => ({
          category: item.category,
          item: item.item,
          critical: item.critical
        })),
        require_equipment_id: formData.require_equipment_id,
        require_mileage_hours: formData.require_mileage_hours,
        require_location: formData.require_location
      });
      
      toast.success(t('adminToasts.InspectionTemplateCreatedSuccessfully'));
      setShowCreateDialog(false);
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(t('adminToasts.FailedToCreateTemplate'));
    } finally {
      setSubmitting(false);
    }
  };

  const updateTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error(t('adminToasts.PleaseEnterATemplateName'));
      return;
    }
    if (checklistItems.length === 0) {
      toast.error(t('adminToasts.PleaseAddAtLeastOneChecklistItem'));
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.put(`/inspections/custom-templates/${selectedTemplate.id}`, {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        checklist: checklistItems.map(item => ({
          category: item.category,
          item: item.item,
          critical: item.critical
        })),
        require_equipment_id: formData.require_equipment_id,
        require_mileage_hours: formData.require_mileage_hours,
        require_location: formData.require_location
      });
      
      toast.success(t('adminToasts.TemplateUpdatedSuccessfully'));
      setShowEditDialog(false);
      loadTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error(t('adminToasts.FailedToUpdateTemplate'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axiosInstance.delete(`/inspections/custom-templates/${templateId}`);
      toast.success(t('adminToasts.TemplateDeleted'));
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(t('adminToasts.FailedToDeleteTemplate'));
    }
  };

  const previewTemplate = async (template) => {
    if (template.is_builtin) {
      // For built-in templates, use the templates endpoint
      try {
        const res = await axiosInstance.get(`/inspections/templates/${template.id}`);
        setSelectedTemplate({ ...res.data, is_builtin: true });
        setShowPreviewDialog(true);
      } catch (error) {
        toast.error(t('adminToasts.FailedToLoadTemplate'));
      }
    } else {
      // For custom templates
      try {
        const res = await axiosInstance.get(`/inspections/custom-templates/${template.id}`);
        setSelectedTemplate({ ...res.data, is_custom: true });
        setShowPreviewDialog(true);
      } catch (error) {
        toast.error(t('adminToasts.FailedToLoadTemplate'));
      }
    }
  };

  // Group checklist items by category for preview
  const groupedChecklist = selectedTemplate?.checklist?.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {}) || {};

  // Group form checklist items by category
  const groupedFormChecklist = checklistItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const iconOptions = [
    { value: 'clipboard', label: 'Clipboard' },
    { value: 'truck', label: 'Truck' },
    { value: 'crane', label: 'Crane/Lift' },
    { value: 'building', label: 'Building/Structure' },
    { value: 'wrench', label: 'Tools' },
    { value: 'hard-hat', label: 'Hard Hat' },
    { value: 'fire', label: 'Fire/Safety' },
    { value: 'electric', label: 'Electrical' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('adminUI.EquipmentInspectionsForms')}</h1>
            <p className="text-gray-600">{t('adminUI.ManageInspectionTemplatesAndOperatorEvaluationForms')}</p>
          </div>
        </div>

        <Tabs defaultValue="templates" className="space-y-6">
          <TabsList>
            <TabsTrigger value="templates">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Inspection Templates
            </TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="evaluations-tab">
              <FileText className="w-4 h-4 mr-2" />
              Evaluation Forms
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates">
            <div className="flex justify-end mb-4">
              <Button onClick={openCreateDialog} className="bg-red-600 hover:bg-red-700">
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>

        {/* Custom Templates */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Your Custom Templates
            </CardTitle>
            <CardDescription>{t('adminUI.TemplatesYouVeCreatedForYourCompany')}</CardDescription>
          </CardHeader>
          <CardContent>
            {templates.custom.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">{t('adminUI.NoCustomTemplatesYet')}</p>
                <Button onClick={openCreateDialog} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Template
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.custom.map(template => (
                  <Card key={template.id} className="border hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold">{template.name}</h3>
                          <p className="text-sm text-gray-500">{template.checklist_count} items</p>
                        </div>
                        <Badge variant="secondary">{t('adminUI.Custom')}</Badge>
                      </div>
                      {template.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => previewTemplate(template)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(template)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteTemplate(template.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Built-in Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Built-in Templates
            </CardTitle>
            <CardDescription>{t('adminUI.PreConfiguredOshaCompliantInspectionTemplates')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.builtin.map(template => (
                <Card key={template.id} className="border bg-gray-50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold">{template.name}</h3>
                        <p className="text-sm text-gray-500">{template.checklist_count} items</p>
                      </div>
                      <Badge>{t('adminUI.BuiltIn')}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                    <Button size="sm" variant="outline" onClick={() => previewTemplate(template)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="evaluations">
            <OperatorEvaluation user={user} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Template Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {showEditDialog ? 'Edit Inspection Template' : 'Create Inspection Template'}
            </DialogTitle>
            <DialogDescription>{t('adminUI.DesignACustomInspectionChecklistForYourTeam')}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">{t('adminUI.TemplateDetails')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>{t('adminUI.TemplateName')}</Label>
                    <Input
                      placeholder="e.g., Concrete Pump Inspection"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>{t('adminUI.Description')}</Label>
                    <Textarea
                      placeholder="Describe when this inspection should be used..."
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Icon</Label>
                    <Select value={formData.icon} onValueChange={(v) => setFormData({...formData, icon: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('adminUI.Category')}</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom</SelectItem>
                        <SelectItem value="heavy_equipment">Heavy Equipment</SelectItem>
                        <SelectItem value="vehicles">Vehicles</SelectItem>
                        <SelectItem value="structures">Structures</SelectItem>
                        <SelectItem value="equipment">Equipment</SelectItem>
                        <SelectItem value="ppe">PPE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Required Fields */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-sm font-medium text-gray-600">{t('adminUI.RequiredFields')}</h4>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="req-equipment">{t('adminUI.RequireEquipmentId')}</Label>
                    <Switch
                      id="req-equipment"
                      checked={formData.require_equipment_id}
                      onCheckedChange={(v) => setFormData({...formData, require_equipment_id: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="req-mileage">{t('adminUI.RequireMileageHours')}</Label>
                    <Switch
                      id="req-mileage"
                      checked={formData.require_mileage_hours}
                      onCheckedChange={(v) => setFormData({...formData, require_mileage_hours: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="req-location">{t('adminUI.RequireLocation')}</Label>
                    <Switch
                      id="req-location"
                      checked={formData.require_location}
                      onCheckedChange={(v) => setFormData({...formData, require_location: v})}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Checklist Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">Checklist Items ({checklistItems.length})</h3>
                </div>

                {/* Add New Item */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3">{t('adminUI.AddChecklistItem')}</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>{t('adminUI.Category')}</Label>
                          <Input
                            placeholder="e.g., Safety, Fluids, Controls"
                            value={newItem.category}
                            onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex items-center gap-2">
                            <Switch
                              id="new-critical"
                              checked={newItem.critical}
                              onCheckedChange={(v) => setNewItem({...newItem, critical: v})}
                            />
                            <Label htmlFor="new-critical" className="text-sm">{t('adminUI.CriticalItem')}</Label>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label>{t('adminUI.InspectionItem')}</Label>
                        <Input
                          placeholder="e.g., Check fluid levels are adequate"
                          value={newItem.item}
                          onChange={(e) => setNewItem({...newItem, item: e.target.value})}
                          onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                        />
                      </div>
                      <Button onClick={addChecklistItem} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Current Items */}
                {checklistItems.length > 0 && (
                  <div className="space-y-3">
                    {Object.entries(groupedFormChecklist).map(([category, items]) => (
                      <div key={category}>
                        <h4 className="text-sm font-medium text-gray-600 mb-2">{category}</h4>
                        <div className="space-y-2">
                          {items.map((item, idx) => {
                            const globalIdx = checklistItems.findIndex(i => i.id === item.id);
                            return (
                              <div key={item.id} className="flex items-center gap-2 p-2 border rounded bg-white">
                                <GripVertical className="h-4 w-4 text-gray-400" />
                                <span className="flex-1 text-sm">{item.item}</span>
                                {item.critical && (
                                  <Badge variant="destructive" className="text-xs">{t('adminUI.Critical')}</Badge>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleItemCritical(globalIdx)}
                                  className={item.critical ? 'text-red-600' : 'text-gray-400'}
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeChecklistItem(globalIdx)}
                                  className="text-gray-400 hover:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-gray-50">
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
            }}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={showEditDialog ? updateTemplate : createTemplate}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                showEditDialog ? 'Update Template' : 'Create Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-4 py-4">
              {/* Template Info */}
              <div className="flex items-center gap-2 mb-4">
                <Badge variant={selectedTemplate?.is_builtin ? 'default' : 'secondary'}>
                  {selectedTemplate?.is_builtin ? 'Built-in' : 'Custom'}
                </Badge>
                <span className="text-sm text-gray-500">
                  {selectedTemplate?.checklist?.length || 0} checklist items
                </span>
              </div>

              {/* Checklist Preview */}
              {Object.entries(groupedChecklist).map(([category, items]) => (
                <div key={category}>
                  <h3 className="font-semibold text-gray-700 mb-2">{category}</h3>
                  <div className="space-y-2">
                    {items.map(item => (
                      <div key={item.id} className="flex items-center gap-3 p-2 border rounded">
                        <div className="h-5 w-5 rounded border flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-gray-300" />
                        </div>
                        <span className="flex-1 text-sm">{item.item}</span>
                        {item.critical && (
                          <Badge variant="destructive" className="text-xs">{t('adminUI.Critical')}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInspectionTemplates;
